import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

export interface UserProfile {
  user_id: string;
  name: string | null;
  email: string;
  email_verified: boolean;
  phone: string | null;
  phone_verified: boolean;
  profile_picture_url: string | null;
  interaction_mode: 'text' | 'voice';
  voice_id: string | null;
  /** Preferred SAM voice gender; the backend resolves it to the default voice id for that gender. */
  voice_gender: 'male' | 'female' | null;
  onboarding_completed: boolean;
  theme_mode: 'light' | 'dark' | 'system' | null;
  role_names: string[];
}

export interface VoiceOption {
  id: string;
  name: string;
  gender: string;
  description: string;
  sample_url: string;
}

export interface VoiceConfig {
  interaction_mode_default: 'text' | 'voice';
  allowed_interaction_modes: Array<'text' | 'voice'>;
  voice_provider: string;
  default_voice: string;
  available_voices: VoiceOption[];
}

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

export interface UpdateProfileBody {
  name?: string;
  profile_picture_url?: string;
  interaction_mode?: 'text' | 'voice';
  voice_id?: string;
  voice_gender?: 'male' | 'female';
  theme_mode?: 'light' | 'dark' | 'system';
}

export interface TenantVoiceSettings {
  interaction_mode_default?: 'text' | 'voice';
  allowed_interaction_modes?: Array<'text' | 'voice'>;
  default_voice?: string;
}

export interface VoiceCatalogueData {
  key: string;
  effective_value: VoiceConfig;
  system_value: Partial<VoiceConfig>;
  tenant_value: TenantVoiceSettings;
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Me', 'VoiceConfig'],
  endpoints: (builder) => ({
    getMe: builder.query<UserProfile, void>({
      query: () => '/api/v1/users/me',
      transformResponse: (res: ApiResponse<UserProfile>) => res.data,
      providesTags: ['Me'],
    }),

    updateMe: builder.mutation<UserProfile, UpdateProfileBody>({
      query: (body) => ({ url: '/api/v1/users/me', method: 'PUT', body }),
      transformResponse: (res: ApiResponse<UserProfile>) => res.data,
      invalidatesTags: ['Me'],
    }),

    completeUserOnboarding: builder.mutation<UserProfile, void>({
      query: () => ({ url: '/api/v1/users/me/complete-onboarding', method: 'POST' }),
      transformResponse: (res: ApiResponse<UserProfile>) => res.data,
      invalidatesTags: ['Me'],
    }),

    requestEmailChange: builder.mutation<{ method: string }, { new_email: string }>({
      query: (body) => ({ url: '/api/v1/users/me/email/request-change', method: 'POST', body }),
      transformResponse: (res: ApiResponse<{ method: string }>) => res.data,
    }),

    verifyEmailChange: builder.mutation<UserProfile, { otp: string }>({
      query: (body) => ({ url: '/api/v1/users/me/email/verify-change', method: 'POST', body }),
      transformResponse: (res: ApiResponse<UserProfile>) => res.data,
      invalidatesTags: ['Me'],
    }),

    requestPhoneChange: builder.mutation<void, { new_phone: string }>({
      query: (body) => ({ url: '/api/v1/users/me/phone/request-change', method: 'POST', body }),
    }),

    verifyPhoneChange: builder.mutation<UserProfile, { otp: string }>({
      query: (body) => ({ url: '/api/v1/users/me/phone/verify-change', method: 'POST', body }),
      transformResponse: (res: ApiResponse<UserProfile>) => res.data,
      invalidatesTags: ['Me'],
    }),

    getVoiceConfig: builder.query<VoiceConfig, void>({
      query: () => '/api/v1/users/voice-config',
      transformResponse: (res: ApiResponse<VoiceConfig>) => res.data,
      providesTags: ['VoiceConfig'],
    }),

    getVoiceCatalogueConfig: builder.query<VoiceCatalogueData, void>({
      query: () => '/api/v1/configurations/voice',
      transformResponse: (res: ApiResponse<VoiceCatalogueData>) => res.data,
      providesTags: ['VoiceConfig'],
    }),

    updateVoiceTenantConfig: builder.mutation<void, TenantVoiceSettings>({
      query: (value) => ({ url: '/api/v1/configurations/voice', method: 'PUT', body: { value } }),
      invalidatesTags: ['VoiceConfig'],
    }),

    uploadAvatar: builder.mutation<string, FormData>({
      query: (formData) => ({ url: '/api/v1/uploads/avatar', method: 'POST', body: formData }),
      transformResponse: (res: ApiResponse<{ url: string }>) => res.data.url,
    }),
  }),
});

export const {
  useGetMeQuery,
  useUpdateMeMutation,
  useCompleteUserOnboardingMutation,
  useRequestEmailChangeMutation,
  useVerifyEmailChangeMutation,
  useRequestPhoneChangeMutation,
  useVerifyPhoneChangeMutation,
  useGetVoiceConfigQuery,
  useGetVoiceCatalogueConfigQuery,
  useUpdateVoiceTenantConfigMutation,
  useUploadAvatarMutation,
} = usersApi;
